import React, { useState, useEffect } from 'react';
import { Award, Building2, ChevronLeft, ChevronRight, Plus } from 'lucide-react';

// Constante para Cloudflare R2
const CLOUDFLARE_PROPERTIES_URL = 'https://pub-e51257e7234a40ea96269a0ac4d1c6ab.r2.dev';

// Función para formatear precios con comas
const formatPrice = (price) => {
  if (!price) return '0';
  return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const PropertyManager = ({ onPropertyUpdate }) => {
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [properties, setProperties] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const propertiesPerPage = 6;

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          throw new Error('No se encontró token de autenticación');
        }
        
        // Obtener datos del usuario
        const userResponse = await fetch(
          `https://backend-production-1786.up.railway.app/api/vendedores/perfil`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json'
            }
          }
        );
        
        if (!userResponse.ok) {
          throw new Error('Error al obtener información del usuario');
        }
        
        const userData = await userResponse.json();
        const username = userData.nombre_usuario;
        
        if (!username) {
          throw new Error('No se pudo determinar el nombre de usuario');
        }
        
        console.log('Nombre de usuario obtenido:', username);
  
        // Obtener propiedades en venta y alquileres por separado
        const [propertiesResponse, rentalsResponse] = await Promise.all([
          fetch(`https://backend-production-1786.up.railway.app/api/propiedades/vendedor/${username}/public`, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }),
          fetch(`https://backend-production-1786.up.railway.app/api/alquileres/vendedor/${username}/public`, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          })
        ]);

        let propertiesData = [];
        let rentalsData = [];

        if (propertiesResponse.ok) {
          const propertiesResult = await propertiesResponse.json();
          propertiesData = Array.isArray(propertiesResult) ? propertiesResult : 
                          (propertiesResult.propiedades || []);
          
          // Asegurarse de que tengan el campo operacion correcto
          propertiesData = propertiesData.map(property => ({
            ...property,
            operacion: 'venta'
          }));
          
          console.log('Propiedades en venta cargadas:', propertiesData.length);
        } else {
          console.warn('No se pudieron cargar las propiedades en venta');
        }

        if (rentalsResponse.ok) {
          const rentalsResult = await rentalsResponse.json();
          rentalsData = Array.isArray(rentalsResult) ? rentalsResult : 
                       (rentalsResult.propiedades || []);
          
          // Asegurarse de que tengan el campo operacion correcto
          rentalsData = rentalsData.map(rental => ({
            ...rental,
            operacion: 'alquiler'
          }));
          
          console.log('Alquileres cargados:', rentalsData.length);
        } else {
          console.warn('No se pudieron cargar los alquileres');
        }

        // Combinar propiedades y alquileres
        const allProperties = [...propertiesData, ...rentalsData];
        console.log('Total propiedades y alquileres:', allProperties.length);
  
        // Cargar propiedades destacadas
        const featuredResponse = await fetch('https://backend-production-1786.up.railway.app/api/mis-propiedades/destacadas', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
  
        let featuredIds = [];
        if (featuredResponse.ok) {
          const featuredData = await featuredResponse.json();
          console.log('Propiedades destacadas cargadas:', featuredData.length);
          featuredIds = featuredData.map(prop => prop.id);
        } else {
          console.warn('No se pudieron cargar las propiedades destacadas');
        }
  
        // Establecer todas las propiedades
        setProperties(allProperties || []);
  
        // Establecer propiedades destacadas
        setSelectedProperties(featuredIds);
  
        setLoading(false);
      } catch (error) {
        console.error('Error cargando propiedades:', error);
        setError(error.message || 'Error al cargar propiedades');
        setLoading(false);
      }
    };
  
    fetchProperties();
  }, []);

  const handleSaveFeaturedProperties = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch('https://backend-production-1786.up.railway.app/api/vendedores/featured-properties', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ propertyIds: selectedProperties })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al guardar propiedades destacadas');
      }

      // Notify parent component
      onPropertyUpdate?.(selectedProperties);
      
      // Exit edit mode
      setEditMode(false);
    } catch (error) {
      console.error('Error guardando propiedades destacadas:', error);
      alert(error.message);
    }
  };

  const indexOfLastProperty = currentPage * propertiesPerPage;
  const indexOfFirstProperty = indexOfLastProperty - propertiesPerPage;
  const currentProperties = properties.slice(indexOfFirstProperty, indexOfLastProperty);
  const totalPages = Math.ceil(properties.length / propertiesPerPage);

  const handlePropertyToggle = (propertyId) => {
    setSelectedProperties(prev => {
      const newSelection = prev.includes(propertyId)
        ? prev.filter(id => id !== propertyId)
        : [...prev, propertyId];
      
      // Limit to maximum 9 featured properties
      return newSelection.length <= 9 ? newSelection : prev;
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 mt-8">
        <div className="flex justify-center">
          <div className="w-8 h-8 animate-spin rounded-full border-4 border-yellow-500 border-t-transparent" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 mt-8 text-center">
        <p className="text-red-500">{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
        >
          Reintentar
        </button>
      </div>
    );
  }

  // Renderizado cuando no hay propiedades destacadas o en modo edición
  if (selectedProperties.length === 0 || editMode) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 mt-8">
        <div className="flex items-center gap-2 mb-6">
          <Building2 className="w-6 h-6 text-yellow-600" />
          <h2 className="text-2xl font-bold text-gray-800">
            {selectedProperties.length === 0 
              ? 'Agregar Propiedades Destacadas' 
              : 'Editar Propiedades Destacadas'}
          </h2>
        </div>
        
        {properties.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            No hay propiedades disponibles para destacar
          </div>
        ) : (
          <>
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Selecciona hasta 9 propiedades para destacar en tu perfil.
                Actualmente seleccionadas: {selectedProperties.length}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {currentProperties.map(property => (
                <PropertyCard 
                  key={property.id} 
                  property={property}
                  isSelected={selectedProperties.includes(property.id)}
                  onToggle={() => handlePropertyToggle(property.id)}
                  selectedCount={selectedProperties.length}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-4 mt-6">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg border transition-colors
                    ${currentPage === 1 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                      : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <span className="text-sm text-gray-600">
                  Página {currentPage} de {totalPages}
                </span>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg border transition-colors
                    ${currentPage === totalPages 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                      : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}

            <div className="flex justify-end mt-6 space-x-4">
              {editMode && (
                <button
                  onClick={() => setEditMode(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                >
                  Cancelar
                </button>
              )}
              <button
                onClick={handleSaveFeaturedProperties}
                disabled={selectedProperties.length === 0}
                className={`px-4 py-2 rounded 
                  ${selectedProperties.length === 0 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-yellow-500 text-white hover:bg-yellow-600'}`}
              >
                Guardar Destacadas
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // Renderizado cuando hay propiedades destacadas y no está en modo edición
  return (
    <div className="bg-white rounded-xl shadow-lg p-8 mt-8">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <Building2 className="w-6 h-6 text-yellow-600" />
          <h2 className="text-2xl font-bold text-gray-800">Propiedades Destacadas</h2>
        </div>
        <button
          onClick={() => setEditMode(true)}
          className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
        >
          <Plus className="w-5 h-5" />
          Editar
        </button>
      </div>

      {selectedProperties.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          No hay propiedades destacadas. Haz clic en "Editar" para añadir.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {properties
            .filter(prop => selectedProperties.includes(prop.id))
            .map(property => (
              <PropertyCard 
                key={property.id} 
                property={property}
                isSelected={true}
                hideToggle={true}
              />
            ))
          }
        </div>
      )}
    </div>
  );
};

const PropertyCard = ({ 
    property, 
    isSelected, 
    onToggle, 
    selectedCount = 0,
    hideToggle = false 
  }) => {
    const [imageError, setImageError] = useState(false);
    
    // Determinar si es alquiler o venta
    const isRental = property.operacion === 'alquiler' || 
                    property.tipo_operacion === 'alquiler';
    
    // Determinar el precio a mostrar
    const getPrice = () => {
      if (isRental) {
        return property.precio_mensual || property.precio;
      } else {
        return property.precio_soles || property.precio;
      }
    };
    
    // Procesar la URL de la imagen para usar Cloudflare R2
    const getImageUrl = (imagePath) => {
      // Si ya es una URL completa, devolverla
      if (imagePath.startsWith('http')) {
        return imagePath;
      }
      
      // Limpiar la ruta
      const cleanPath = imagePath
        .replace(/^\/uploads\//, '')
        .replace(/^uploads\//, '')
        .replace(/^\//, '');
      
      // Devolver la URL de Cloudflare
      return `${CLOUDFLARE_PROPERTIES_URL}/${cleanPath}`;
    };
    
    // Obtener la primera imagen
    const getFirstImage = () => {
      if (imageError) return '/default-property-image.jpg';
      
      // Intentar obtener la imagen de diferentes fuentes
      if (Array.isArray(property.imagenes) && property.imagenes.length > 0) {
        return getImageUrl(property.imagenes[0]);
      }
      
      if (typeof property.imagenes === 'string' && property.imagenes.trim() !== '') {
        const images = property.imagenes.split(',');
        return getImageUrl(images[0].trim());
      }
      
      if (property.imagen_principal) {
        return getImageUrl(property.imagen_principal);
      }
      
      return '/default-property-image.jpg';
    };
    
    const handleImageError = () => {
      console.error('Error loading image for property:', property.id);
      setImageError(true);
    };
    
    return (
      <div 
        className={`relative rounded-xl overflow-hidden shadow-lg border-2 transition-all duration-300
          ${isSelected ? 'border-yellow-500 transform scale-102' : 'border-transparent hover:border-gray-200'}`}
      >
        <div className="relative h-48">
          <img
            src={getFirstImage()}
            alt={property.titulo || 'Propiedad'}
            className="w-full h-full object-cover"
            onError={handleImageError}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          
          {/* Badge de tipo de operación */}
          <div className={`absolute top-2 left-2 text-white text-xs font-bold px-2 py-1 rounded
                        ${isRental ? 'bg-blue-500' : 'bg-purple-500'}`}>
            {isRental ? 'Alquiler' : 'Venta'}
          </div>
        
          {!hideToggle && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              disabled={!isSelected && selectedCount >= 9}
              className={`absolute top-2 right-2 p-2 rounded-full transition-all duration-300
                ${isSelected 
                  ? 'bg-yellow-500 text-white' 
                  : (selectedCount >= 9 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-white/80 hover:bg-yellow-500 hover:text-white')}`}
            >
              <Award className="w-5 h-5" />
            </button>
          )}
        </div>
  
        <div className="p-4">
          <h3 className="font-semibold text-lg mb-2 line-clamp-1">{property.titulo}</h3>
          <p className="text-sm text-gray-600 line-clamp-2">{property.descripcion_corta || property.descripcion}</p>
        
          <div className="mt-4 flex justify-between items-center">
            <div>
              <span className="text-lg font-bold text-gray-800">
                S/. {formatPrice(getPrice())}
              </span>
              {isRental && <span className="text-sm text-gray-500 ml-1">/mes</span>}
            </div>
            
            <span className={`px-3 py-1 rounded-full text-sm font-medium
              ${isSelected ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}`}>
              {isSelected ? 'Destacado' : 'Regular'}
            </span>
          </div>
        </div>
      </div>
    );
  };

export default PropertyManager;